import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { IconCaretDown, IconSearch } from '@tabler/icons-react'
import { Menu } from 'antd'
import { MenuItemType } from 'antd/lib/menu/hooks/useItems'
import { debounce } from 'lodash'

import { Button } from '@/components/shared-components/BUI/Button'
import { Dropdown } from '@/components/shared-components/BUI/Dropdown'
import { Input } from '@/components/shared-components/BUI/Input'
import { Text } from '@/components/shared-components/BUI/Text'
import { dropdownTitle } from '@/components/shared-components/Filters/DropdownTitle'
import { createCommaSeparatedString } from '@/utils/createCommaSeparated'

import { buildPassTheme } from 'src/styles/theme'

export type GenericResource = {
  id: string
  label: string
  subLabel?: string
}

type Props = {
  searchStringState: ReturnType<typeof useState<string>>
  multiple?: boolean
  loading?: boolean
  items: GenericResource[]
  value?: GenericResource[]
  headerLabel?: string
  emptyLabel?: string
  maxHeight?: number
  renderItem: (item: GenericResource) => React.ReactNode
  onChange?: (value: GenericResource[]) => void
  renderResults?: (items: GenericResource[]) => React.ReactNode
}

export const GenericResourceSelector: React.FC<Props> = ({
  searchStringState: [searchString, setSearchString],
  multiple,
  loading,
  items = [],
  value,
  headerLabel,
  emptyLabel = 'Select',
  maxHeight = 400,
  renderItem,
  renderResults,
  onChange,
}) => {
  const [open, toggleOpen] = useState(false)

  const onItemSelected = useCallback(
    (item: GenericResource) => {
      if (!multiple) {
        onChange?.([item])
        toggleOpen(false)
      } else {
        if (value?.some((v) => v.id === item.id)) {
          onChange?.(value.filter((v) => v.id !== item.id))
        } else {
          onChange?.([...(value ?? []), item])
        }
      }
    },
    [multiple, onChange, value]
  )

  const onInputChange = useMemo(() => {
    return debounce((e: ChangeEvent<HTMLInputElement>) => {
      setSearchString(e.target.value)
    }, 500)
  }, [setSearchString])

  const valueIds = useMemo(() => {
    return value?.map((v) => v.id) ?? []
  }, [value])

  const missingItems = useMemo(() => {
    if (searchString?.length) return [] // if searching, ignore missing values
    if (!value?.length) return []

    const itemIds = items.map((item) => item.id)

    return value.filter((v) => !itemIds.includes(v.id)) ?? []
  }, [items, searchString, value])

  const allItems = useMemo(() => {
    return [...missingItems, ...items]
  }, [items, missingItems])

  const headerItems = useMemo<MenuItemType[]>(() => {
    if (headerLabel) {
      return [dropdownTitle(headerLabel) as MenuItemType]
    }

    return []
  }, [headerLabel])

  const searchItems = useMemo<MenuItemType[]>(
    () => [
      {
        key: 'search',
        style: { position: 'sticky', top: 0, zIndex: 1 },
        label: (
          <Input
            defaultValue={searchString}
            onChange={onInputChange}
            placeholder="Search"
            allowClear
            prefix={<IconSearch size={16} />}
          />
        ),
      },
    ],
    [onInputChange, searchString]
  )

  const footerItems = useMemo<MenuItemType[]>(() => {
    if (searchString?.length && !loading && !items.length) {
      return [
        {
          key: 'no-results',
          label: <Text variant="caption01">No results found</Text>,
          disabled: true,
        },
      ]
    }

    if (loading) {
      return [
        {
          key: 'loading',
          label: <Text variant="caption01">Loading...</Text>,
          disabled: true,
        },
      ]
    }

    return [
      {
        key: 'more',
        label: <Text variant="caption01">Search for more...</Text>,
        disabled: true,
      },
    ]
  }, [items.length, loading, searchString])

  const menuItems = useMemo<MenuItemType[]>(() => {
    return [
      ...headerItems,
      ...searchItems,
      ...allItems.map(
        (item) =>
          ({
            key: item.id,
            label: renderItem(item),
            onClick: () => onItemSelected(item),
            style: valueIds.includes(item.id)
              ? { background: buildPassTheme.colour.primary._50 }
              : {},
          }) as MenuItemType
      ),
      ...footerItems,
    ]
  }, [
    allItems,
    headerItems,
    footerItems,
    onItemSelected,
    renderItem,
    searchItems,
    valueIds,
  ])

  return (
    <Dropdown
      overlay={
        <Menu items={menuItems} style={{ maxHeight, overflow: 'auto' }} />
      }
      placement="bottomLeft"
      trigger={['click']}
      open={open}
      onOpenChange={toggleOpen}
    >
      <Button variant="secondary">
        {value?.length
          ? renderResults
            ? renderResults(value)
            : createCommaSeparatedString(value.map((v) => v.label))
          : emptyLabel}

        <IconCaretDown
          color={buildPassTheme.colour.secondary.base}
          fill={buildPassTheme.colour.secondary.base}
          size={16}
        />
      </Button>
    </Dropdown>
  )
}
